import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { toast } from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Form, Row, Col, Input, Select, Button,
  TimePicker, message, Typography, Tag, Avatar, Flex
} from "antd";
import {
  EditOutlined, UserOutlined, MailOutlined, PhoneOutlined,
  EnvironmentOutlined, MedicineBoxOutlined, CalendarOutlined,
  ClockCircleOutlined, TrophyOutlined,
  ScheduleOutlined, CheckCircleOutlined, TeamOutlined
} from "@ant-design/icons";
import Layout from "../../components/layout";

const { Title, Text } = Typography;
const { Option } = Select;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("schedule-dr-styles")) return;
  const el = document.createElement("style");
  el.id = "schedule-dr-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .sd-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .sd-wrap { max-width: 960px; margin: 0 auto; padding: 32px 24px 64px; }

    /* ── Hero ── */
    .sd-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 32px;
    }
    .sd-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .sd-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -40px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }
    .sd-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    /* ── Card sections ── */
    .sd-section {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 28px 28px 12px; margin-bottom: 20px;
    }
    .sd-section-head {
      display: flex; align-items: center; gap: 10px; margin-bottom: 24px;
      padding-bottom: 16px; border-bottom: 1.5px solid #f1f5f9;
    }
    .sd-section-icon {
      width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center; font-size: 16px;
    }

    /* ── Form item spacing ── */
    .sd-root .ant-form-item {
      margin-bottom: 20px !important;
    }
    .sd-root .ant-form-item-label {
      padding-bottom: 4px !important; line-height: 1 !important;
    }
    .sd-root .ant-form-item-label > label {
      font-size: 12px !important; font-weight: 600 !important;
      color: ${SLATE_MID} !important; letter-spacing: .2px;
      height: 20px !important; line-height: 20px !important;
    }
    /* keep error message from pushing layout */
    .sd-root .ant-form-item-explain {
      min-height: 0 !important; font-size: 11px !important;
    }

    /* ── Shared control height token: 44px ── */

    /* Plain input (no suffix/prefix) */
    .sd-root .ant-input {
      border-radius: 10px !important; height: 44px !important; line-height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sd-root .ant-input:hover,
    .sd-root .ant-input:focus {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .sd-root .ant-input[readonly] {
      background: ${SURFACE} !important; color: ${MUTED} !important;
    }

    /* Wrapper input (with suffix icon) */
    .sd-root .ant-input-affix-wrapper {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sd-root .ant-input-affix-wrapper .ant-input {
      height: 100% !important; border: none !important; box-shadow: none !important;
      padding: 0 !important; background: transparent !important;
    }
    .sd-root .ant-input-affix-wrapper:hover,
    .sd-root .ant-input-affix-wrapper-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .sd-root .ant-input-affix-wrapper:has(.ant-input[readonly]) {
      background: ${SURFACE} !important; color: ${MUTED} !important;
    }

    /* Number input — remove spinner arrows */
    .sd-root input[type="number"]::-webkit-inner-spin-button,
    .sd-root input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
    .sd-root input[type="number"] { -moz-appearance: textfield; }

    /* Select (single) */
    .sd-root .ant-select:not(.ant-select-multiple) .ant-select-selector {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sd-root .ant-select:not(.ant-select-multiple) .ant-select-selection-item,
    .sd-root .ant-select:not(.ant-select-multiple) .ant-select-selection-placeholder {
      line-height: 42px !important;
    }
    .sd-root .ant-select:not(.ant-select-multiple).ant-select-focused .ant-select-selector,
    .sd-root .ant-select:not(.ant-select-multiple) .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Select (multi) — grows with content */
    .sd-root .ant-select-multiple .ant-select-selector {
      border-radius: 10px !important; min-height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 4px 8px !important;
      display: flex !important; align-items: center !important; flex-wrap: wrap !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sd-root .ant-select-multiple.ant-select-focused .ant-select-selector,
    .sd-root .ant-select-multiple .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* TimePicker */
    .sd-root .ant-picker {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; width: 100% !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sd-root .ant-picker-input > input { font-size: 13px !important; }
    .sd-root .ant-picker:hover, .sd-root .ant-picker-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* ── Doctor preview chip ── */
    .sd-doctor-chip {
      background: ${SURFACE}; border: 1.5px solid #e2e8f0;
      border-radius: 14px; padding: 14px 18px; margin-top: 4px; margin-bottom: 8px;
      display: flex; align-items: center; gap: 14px;
      transition: border-color .2s, background .2s;
    }
    .sd-doctor-chip.active { border-color: ${TEAL}; background: #f0fdfb; }

    /* ── Submit button ── */
    .sd-submit {
      height: 52px !important; padding: 0 44px !important;
      border-radius: 14px !important;
      font-size: 15px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; color: ${WHITE} !important;
      box-shadow: 0 8px 20px ${TEAL}44 !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .sd-submit:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 14px 32px ${TEAL}55 !important;
    }

    /* ── Animations ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .sd-fade   { animation: fadeUp .5s ease both; }
    .sd-fade-1 { animation-delay: .05s; }
    .sd-fade-2 { animation-delay: .12s; }
    .sd-fade-3 { animation-delay: .2s; }
    .sd-fade-4 { animation-delay: .28s; }
  `;
  document.head.appendChild(el);
};

/* ─── Section wrapper ─────────────────────────────────────────── */
function Section({ icon, title, children, className = "" }) {
  return (
    <div className={`sd-section ${className}`}>
      <div className="sd-section-head">
        <div className="sd-section-icon">{icon}</div>
        <Title level={5} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
          {title}
        </Title>
      </div>
      {children}
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────── */
function AdminScheduleDoctor() {
  const [services,       setServices]       = useState([]);
  const [doctors,        setDoctors]        = useState([]);
  const [locations,      setLocations]      = useState([]);
  const [specialitys,    setSpecialitys]    = useState([]);
  const [totalSlots,     setTotalSlots]     = useState([]);
  const [clinicDays,     setClinicDays]     = useState([]);
  const [selectedDays,   setSelectedDays]   = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [form]    = Form.useForm();
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const format    = "HH:mm";

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    axios.get("/api/user/get-all-clinic-day")
      .then(res => { if (res.data.success) setClinicDays(res.data.clinicDays); })
      .catch(() => message.error("Error fetching clinic days"));
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        dispatch(showLoading());
        const res = await axios.get("/api/admin/get-registered-doctors", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        dispatch(hideLoading());
        if (res.data.success) setDoctors(res.data.data);
        else toast.error(res.data.message);
      } catch { dispatch(hideLoading()); toast.error("Failed to fetch doctors"); }
    };
    fetchDoctors();
  }, [dispatch]);

  useEffect(() => {
    const fetchAll = async () => {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      try {
        const [loc, spec, slots, svc] = await Promise.all([
          axios.get("/api/user/get-all-location",            { headers }),
          axios.get("/api/user/get-all-speciality",          { headers }),
          axios.get("/api/Appointmentslot/get-all-totalSlots", { headers }),
          axios.get("/api/user/get-all-services",            { headers }),
        ]);
        if (loc.data.success)   setLocations(loc.data.data);
        if (spec.data.success)  setSpecialitys(spec.data.data);
        if (slots.data.success) setTotalSlots(slots.data.data);
        if (svc.data.success)   setServices(svc.data.data);
      } catch (e) { console.error(e); }
    };
    fetchAll();
  }, []);

  const handleDoctorChange = (doctorId) => {
    const doc = doctors.find(d => d._id === doctorId);
    setSelectedDoctor(doc || null);
    if (doc) {
      const firstName = doc.firstName || (doc.name ? doc.name.split(" ")[0] : "");
      const lastName  = doc.lastName  || (doc.name ? doc.name.split(" ").slice(1).join(" ") : "");
      form.setFieldsValue({ firstName, lastName, email: doc.email || "", phoneNumber: doc.phone || "" });
    }
  };




  const onFinish = async (values) => {
    if (selectedDays.length === 0) { message.error("Please select at least one clinic day."); return; }
    try {
      dispatch(showLoading());
      const formattedTimings = values.appointmentTime.map(t => t.format("HH:mm"));
      const res = await axios.post(
        "/api/admin/admin-apply-appointment",
        { ...values, clinicDays: selectedDays.join(", "), appointmentTime: formattedTimings },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success("Doctor appointment scheduled successfully");
        setSelectedDays([]); setSelectedDoctor(null); form.resetFields();
        navigate("/");
      } else { toast.error(res.data.message); }
    } catch { dispatch(hideLoading()); toast.error("Something went wrong"); }
  };

  const validateTiming = (_, value) => {
    if (value && value[0] && value[1] &&
      value[0].format(format) !== "00:00" && value[1].format(format) !== "00:00") {
      return Promise.resolve();
    }
    return Promise.reject("Please select valid appointment timing");
  };

  return (
    <Layout>
      <div className="sd-root">
        <div className="sd-wrap">

          {/* ── Hero ── */}
          <div className="sd-hero sd-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="sd-eyebrow">
                <ScheduleOutlined style={{ marginRight: 5 }} />Scheduling
              </div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Schedule a Doctor
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Assign a doctor to a service, set clinic days, timings, and consultation details
              </Text>
            </div>
          </div>

          <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>

            {/* ── Section 1 : Service & Doctor ── */}
            <Section icon={<TeamOutlined />} title="Select Service & Doctor" className="sd-fade sd-fade-1">
              {/* Two equal columns */}
              <Row gutter={[20, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item label="Specialty / Service" name="service"
                    rules={[{ required: true, message: "Please select a service!" }]}>
                 <Select
  showSearch
  optionFilterProp="children"
  placeholder="Choose a service"
  suffixIcon={<MedicineBoxOutlined style={{ color: MUTED }} />}
 filterOption={(input, option) =>
  (option?.children ?? "")
    .toString()
    .toLowerCase()
    .includes(input.toLowerCase())
}
>
  {services.map(s => (
    <Option key={s.service} value={s.service}>
      {s.service}
    </Option>
  ))}
</Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Doctor" name="doctor"
                    rules={[{ required: true, message: "Please select a doctor!" }]}>
                    <Select
  showSearch
  optionFilterProp="children"
  placeholder="Choose a doctor"
  onChange={handleDoctorChange}
  suffixIcon={<UserOutlined style={{ color: MUTED }} />}
 filterOption={(input, option) =>
  (option?.children ?? "")
    .toString()
    .toLowerCase()
    .includes(input.toLowerCase())
}
>
  {doctors.map(d => (
    <Option key={d._id} value={d._id}>
      {d.name || `${d.firstName} ${d.lastName}`}
    </Option>
  ))}
</Select>
                  </Form.Item>
                </Col>
              </Row>

              {selectedDoctor && (
                <div className="sd-doctor-chip active">
                  <Avatar size={44} style={{ background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`, fontSize: 18, flexShrink: 0 }}>
                    {selectedDoctor.firstName?.[0] || selectedDoctor.name?.[0]}
                  </Avatar>
                  <div>
                    <Text strong style={{ color: SLATE, display: "block", fontSize: 14 }}>
                      Dr. {selectedDoctor.firstName || selectedDoctor.name} {selectedDoctor.lastName || ""}
                    </Text>
                    <Text style={{ color: MUTED, fontSize: 12 }}>{selectedDoctor.email}</Text>
                  </div>
                  <Tag style={{ marginLeft: "auto", background: TEAL_LIGHT, color: EMERALD, border: "none", borderRadius: 20, fontWeight: 600 }}>
                    <CheckCircleOutlined style={{ marginRight: 4 }} />Selected
                  </Tag>
                </div>
              )}
            </Section>

            {/* ── Section 2 : Doctor Information ──
                Layout: 3 columns on lg, 2 on sm, 1 on xs
                Row A: First Name | Last Name | Email
                Row B: Phone      | Location  | Slots
            */}
            <Section icon={<UserOutlined />} title="Doctor Information" className="sd-fade sd-fade-2">
              <Row gutter={[20, 0]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="First Name" name="firstName"
                    rules={[{ required: true, message: "Please enter first name!" }]}>
                    <Input placeholder="First name"
                      suffix={<EditOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Last Name" name="lastName"
                    rules={[{ required: true, message: "Please enter last name!" }]}>
                    <Input placeholder="Last name"
                      suffix={<EditOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Email Address" name="email"
                    rules={[{ required: true, message: "Please enter email!" }]}>
                    <Input placeholder="Auto-filled on doctor select" readOnly
                      suffix={<MailOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Phone Number" name="phoneNumber"
                    rules={[{ required: true, message: "Please enter phone number!" }]}>
                    <Input placeholder="Auto-filled on doctor select" readOnly
                      suffix={<PhoneOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Appointment Location" name="location"
                    rules={[{ required: true, message: "Please select a location!" }]}>
                    <Select placeholder="Select location"
                      suffixIcon={<EnvironmentOutlined style={{ color: MUTED }} />}>
                      {locations.map(l => <Option key={l.location} value={l.location}>{l.location}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Appointment Slots" name="totalSlots"
                    rules={[{ required: true, message: "Please select appointment slots!" }]}>
                    <Select placeholder="Select slots"
                      suffixIcon={<CalendarOutlined style={{ color: MUTED }} />}>
                      {totalSlots.map(s => <Option key={s.totalSlots} value={s.totalSlots}>{s.totalSlots}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            {/* ── Section 3 : Professional Information ──
                Row A: Specialization | Experience | Fee
                Row B: Appointment Hours (span 12) | Clinic Days (span 12)
            */}
            <Section icon={<MedicineBoxOutlined />} title="Professional Information" className="sd-fade sd-fade-3">
              <Row gutter={[20, 0]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Specialization" name="specialization"
                    rules={[{ required: true, message: "Please select specialization!" }]}>
                   <Select
  showSearch
  optionFilterProp="children"
  placeholder="Select specialization"
  suffixIcon={<TrophyOutlined style={{ color: MUTED }} />}
filterOption={(input, option) =>
  (option?.children ?? "")
    .toString()
    .toLowerCase()
    .includes(input.toLowerCase())
}
>
  {specialitys.map(s => (
    <Option key={s.speciality} value={s.speciality}>
      {s.speciality}
    </Option>
  ))}
</Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Years of Experience" name="experience"
                    rules={[{ required: true, message: "Please enter experience!" }]}>
                    <Input placeholder="e.g. 5" type="number"
                      suffix={<Text style={{ color: MUTED, fontSize: 11 }}>yrs</Text>} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Consultation Fee (₦)" name="feePerConsultation"
                    rules={[{ required: true, message: "Please enter consultation fee!" }]}>
                    <Input placeholder="e.g. 5000" type="number" />
                  </Form.Item>
                </Col>

                {/* Second row: two halves */}
                <Col xs={24} sm={12}>
                  <Form.Item label="Appointment Hours" name="appointmentTime"
                    rules={[{ validator: validateTiming }]}>
                    <TimePicker.RangePicker
                      format="HH:mm"
                      placeholder={["Start time", "End time"]}
                      suffixIcon={<ClockCircleOutlined style={{ color: MUTED }} />}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Clinic Days" name="clinicDay"
                    rules={[{ required: true, message: "Please select clinic days!" }]}>
                    <Select mode="multiple" allowClear placeholder="Select available days"
                      value={selectedDays} onChange={setSelectedDays}
                      suffixIcon={<CalendarOutlined style={{ color: MUTED }} />}>
                      {clinicDays.map(d => <Option key={d.name} value={d.name}>{d.name}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            {/* ── Submit ── */}
            <div className="sd-fade sd-fade-4">
              <Flex justify="flex-end">
                <Button className="sd-submit" htmlType="submit" icon={<ScheduleOutlined />}>
                  Schedule Appointment
                </Button>
              </Flex>
            </div>

          </Form>
        </div>
      </div>
    </Layout>
  );
}

export default AdminScheduleDoctor;